import { useState, useEffect } from "react";
import { useSiteContent } from "../../../hooks/useSiteContent";

const ContactSection = () => {
  const { content, loading } = useSiteContent();
  const [formData, setFormData] = useState({
    from_name: "",
    from_email: "",
    subject: "",
    body: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const apiKey = content?.messageCenter?.apiKey || "";
  const isApiKeyConfigured = apiKey.trim() !== "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isApiKeyConfigured) {
      setSubmitMessage({
        type: "error",
        text: "El formulario no está configurado. Por favor contacta al administrador.",
      });
      return;
    }

    setSubmitting(true);
    setSubmitMessage(null);

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append("api_key", apiKey);
      formDataToSend.append("from_name", formData.from_name);
      formDataToSend.append("from_email", formData.from_email);
      formDataToSend.append("subject", formData.subject);
      formDataToSend.append("body", formData.body);

      const response = await fetch(
        "https://axloweb.com/axlowebmessagecenter/backend/api/messages/messages_create.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formDataToSend.toString(),
        }
      );

      const result = await response.json();

      if (result.success) {
        setSubmitMessage({
          type: "success",
          text: "¡Mensaje enviado correctamente! Nos pondremos en contacto contigo pronto.",
        });
        setFormData({
          from_name: "",
          from_email: "",
          subject: "",
          body: "",
        });
      } else {
        throw new Error(result.message || "Error al enviar el mensaje");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitMessage({
        type: "error",
        text: "Hubo un error al enviar tu mensaje. Por favor intenta nuevamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center">Cargando información de contacto...</div>
        </div>
      </section>
    );
  }

  const contact = content?.contact;
  const testimonials = content?.testimonials?.filter((t: any) => t.approved) || [];

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Contáctanos</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Estamos listos para ayudarte a hacer crecer tu negocio
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Información de contacto */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Información de Contacto</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <i className="ri-map-pin-line text-2xl text-blue-600"></i>
                  <div>
                    <h4 className="font-semibold text-gray-900">Dirección</h4>
                    <p className="text-gray-600">
                      {contact?.address}, {contact?.city}, {contact?.state} {contact?.zip}
                    </p>
                  </div>
                </div>

                {contact?.phones && contact.phones.length > 0 && (
                  <div className="flex items-start gap-4">
                    <i className="ri-phone-line text-2xl text-blue-600"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900">Teléfonos</h4>
                      {contact.phones.map((phone: any, index: number) => (
                        <p key={index} className="text-gray-600">
                          {phone.label}: {phone.number}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-4">
                  <i className="ri-mail-line text-2xl text-blue-600"></i>
                  <div>
                    <h4 className="font-semibold text-gray-900">Email</h4>
                    <p className="text-gray-600">{contact?.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <i className="ri-time-line text-2xl text-blue-600"></i>
                  <div>
                    <h4 className="font-semibold text-gray-900">Horario</h4>
                    <p className="text-gray-600 whitespace-pre-line">{contact?.business_hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mapa */}
            {contact?.map_query && (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden h-64">
                <iframe
                  src={`https://www.google.com/maps?q=${encodeURIComponent(contact.map_query)}&output=embed`}
                  className="w-full h-full border-0"
                  title="Ubicación"
                  loading="lazy"
                ></iframe>
              </div>
            )}
          </div>

          {/* Formulario de contacto */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Envíanos un Mensaje</h3>

            {!isApiKeyConfigured && (
              <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ El formulario de contacto no está configurado. Por favor contacta al administrador.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="from_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre completo
                </label>
                <input
                  type="text"
                  id="from_name"
                  name="from_name"
                  value={formData.from_name}
                  onChange={handleChange}
                  required
                  disabled={!isApiKeyConfigured}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="from_email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="from_email"
                  name="from_email"
                  value={formData.from_email}
                  onChange={handleChange}
                  required
                  disabled={!isApiKeyConfigured}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Asunto
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  disabled={!isApiKeyConfigured}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-2">
                  Mensaje
                </label>
                <textarea
                  id="body"
                  name="body"
                  value={formData.body}
                  onChange={handleChange}
                  required
                  rows={5}
                  maxLength={500}
                  disabled={!isApiKeyConfigured}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                ></textarea>
                <p className="mt-1 text-sm text-gray-500">
                  {formData.body.length}/500 caracteres
                </p>
              </div>

              {submitMessage && (
                <div
                  className={`p-4 rounded-lg ${
                    submitMessage.type === "success"
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                >
                  {submitMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !isApiKeyConfigured}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium whitespace-nowrap cursor-pointer"
              >
                {submitting ? "Enviando..." : "Enviar Mensaje"}
              </button>
            </form>
          </div>
        </div>

        {/* Testimonios */}
        {testimonials.length > 0 && (
          <div className="mt-20">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Lo que dicen nuestros clientes
            </h3>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {testimonials.map((testimonial: any) => (
                <div key={testimonial.id} className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400"></i>
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">&quot;{testimonial.text}&quot;</p>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-gray-900">{testimonial.author_name}</p>
                    {testimonial.source && (
                      <span className="text-sm text-gray-500">{testimonial.source}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContactSection;
